import {useState, useContext, useEffect} from "react";
import AddTestStepper from "../../components/add/AddTestStepper";
import {
    Box,
    TextField,
    useTheme,
    Grid,
    CircularProgress,
    debounce,
    Button,
    Pagination,
} from "@mui/material";
import AddTestPageNavButtons from "./AddTestPageNavButtons";
import VideoCard from "../global/VideoCard";
import {Video} from "../global/VideoCard";
import {tokens, useMode} from "../../theme";
import {VideoContext} from "../../context/video_context";
import {toast} from "react-toastify";
import LoadBox from "components/global/LoadBox";

type SelectVideosType = {
    handlePageChangeUp: any;
    handlePageChangeDown: any;
    handlePageChange: any;
};

const activeStep: number = 0;

export default function SelectVideos(fn: SelectVideosType) {
    const videoContext = useContext(VideoContext);
    const videos = videoContext?.videos || [];
    const loading = videoContext?.loading || false;
    const getVideos = videoContext?.getVideos || (() => Promise.resolve({videos: [], count: 0}));
    const updateVideosFromChannel = videoContext?.updateVideosFromChannel || (() => Promise.resolve({}));
    const results_per_page = videoContext?.results_per_page || 20;
    const [search, setSearch] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [count, setCount] = useState<number>(0);
    const [height, setHeight] = useState("60vh");
    const totalPages = Math.ceil(count / results_per_page);

    // Effect to update videos from channel on initial load
    useEffect(() => {
        // Wrap the async call in toast.promise for feedback
        toast.promise(
            updateVideosFromChannel().then(() => {
                // Refresh the video list after updating using initial search/page values
                // Note: We directly use the initial state values here (search="", currentPage=1)
                // because this effect only runs once on mount.
                console.log("Ran updateVideosFromChannel");
            }),
            {
                pending: "Checking for new videos...",
                success: "Video list updated from channel.",
                error: "Error updating videos from channel.",
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // console.log({ totalPages, count, results_per_page });
    const searchVideos = (search: string, currentPage: number) => {
        getVideos(search, currentPage).then(({videos, count}: any) => {
            // console.log({ videos, count });
            setCount(count);
        });
    };
    const debouncedSearch = debounce(searchVideos, 750);
    useEffect(() => {
        debouncedSearch(search, currentPage);
    }, [search, currentPage]);

    // Effect to update height
    useEffect(() => {
        const updateHeight = () => {
            setHeight(`${window.innerHeight * 0.7}px`);
        };

        // Initial setting of height
        updateHeight();

        // Update height on window resize
        window.addEventListener("resize", updateHeight);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener("resize", updateHeight);
        };
    }, []);

    return (
        <Box
            sx={{
                width: "100%",
                paddingTop: 2,
                display: "flex",
                flexDirection: "column",
                height: "100vh",
            }}
        >
            <AddTestStepper activeStep={activeStep}/>
            <Box sx={{paddingX: 6, paddingTop: 2, width: "100%"}}>
                <Box
                    sx={{
                        fontSize: "h3.fontSize",
                        marginTop: 2,
                        marginBottom: 1,
                    }}
                >
                    Videos
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: 2,
                    }}
                >
                    <TextField
                        sx={{
                            flex: "8",
                        }}
                        size={"small"}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        placeholder="Search your videos"
                    ></TextField>
                    <Button
                        variant={"contained"}
                        onClick={() => {
                            toast.promise(
                                updateVideosFromChannel().then(() => {
                                    // Use current search and page when button is clicked
                                    searchVideos(search, currentPage);
                                }),
                                {
                                    pending: "Updating Videos...",
                                    success: "Videos Updated",
                                    error: "Error Updating Videos",
                                },
                            );
                        }}
                        style={{
                            flex: "2",
                            height: "100%",
                        }}
                    >
                        Update Video Data From Channel
                    </Button>
                </Box>
                <Box sx={{justifyItems: "center"}}>
                    <LoadBox loading={loading}>
                        <Box sx={{overflow: "auto", height: height}}>
                            <Grid container spacing={1}>
                                {videos.map((video) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            lg={3}
                                            key={video.video_id}
                                            sx={{minWidth: "300px"}}
                                        >
                                            <VideoCard
                                                {...video}
                                                views={video.views || "0"}
                                                uploaded={video.uploaded || new Date()}
                                                handlePageChangeUp={fn.handlePageChangeUp}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                        <Pagination
                            sx={{marginBottom: 2}}
                            count={totalPages}
                            page={currentPage}
                            onChange={(e, page) => {
                                setCurrentPage(page);
                            }}
                        />
                    </LoadBox>
                </Box>
            </Box>

            {/*<AddTestPageNavButtons*/}
            {/*  step={activeStep}*/}
            {/*  handlePageChangeUp={fn.handlePageChangeUp}*/}
            {/*  handlePageChangeDown={fn.handlePageChangeDown}*/}
            {/*/>*/}
        </Box>
    );
}